import { type CSSResultGroup, html, LitElement, nothing, unsafeCSS } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { repeat } from 'lit/directives/repeat.js'
import { isEscape } from '@/navbar/utils/eventKeys'
import { emit } from '@/navbar/utils'
import { $t, localized } from '@/navbar/i18n'
import { logWarn } from '@/utils/errorReporter'
import styles from './eco-suite-navbar-skip-to-content.scss?inline'

export type ResolvedTarget = {
  label: string
  target: string | HTMLElement
}

/**
 * Renders a visually-hidden skip-link trigger and a popover of jump targets,
 * allowing keyboard users to bypass the navbar and jump to key page sections.
 *
 * When the trigger receives focus, the popover becomes visible and focus moves
 * to the first link. Clicking a link focuses the target element. Pressing
 * Escape hides the popover, restores focus to the trigger, and emits
 * `eco-dismissed` so parent components can redirect focus
 * further (e.g. to the app-switcher).
 *
 * Renders nothing when `targets` is empty.
 *
 * @internal
 *
 * @event eco-dismissed - Fired when the user dismisses the
 *   popover via Escape. The parent is free to move focus elsewhere after
 *   receiving this event; the component restores focus to its own trigger
 *   first as a safe default.
 */
@localized()
@customElement('eco-suite-navbar-skip-to-content')
export class EcoSuiteNavbarSkipToContent extends LitElement {
  static readonly styles: CSSResultGroup = [unsafeCSS(styles)]
  private static readonly _popoverId = 'skip-to-content-popover'
  private _dismissing = false

  @query('.SkipToContent__trigger')
  private readonly triggerEl?: HTMLAnchorElement

  @query('.SkipToContent__link')
  private readonly firstLink?: HTMLButtonElement

  /**
   * The list of resolved jump targets to display in the popover. Each entry
   * has a `label` (visible link text) and a `target` (CSS selector string or
   * direct `HTMLElement` reference). Provided by `<eco-suite-navbar>` via its
   * `resolvedSkipTargets` getter. When empty, the component renders nothing.
   */
  @property({ attribute: false })
  targets: ResolvedTarget[] = []

  @state()
  private visible = false

  private async handleTriggerFocus() {
    if (this.visible || this._dismissing) return
    this.visible = true
    await this.updateComplete
    this.firstLink?.focus()
  }

  private handlePopoverFocusout(e: FocusEvent) {
    const related = e.relatedTarget as Node | null
    if (!related || !this.shadowRoot?.contains(related)) {
      this.visible = false
    }
  }

  private handleKeydown(e: KeyboardEvent) {
    if (isEscape(e)) {
      this.visible = false
      emit(this, 'eco-dismissed')
      this._dismissing = true
      this.triggerEl?.focus()
      this._dismissing = false
    }
  }

  private handleSkip(target: string | HTMLElement) {
    this.visible = false

    const element = typeof target === 'string'
      ? document.querySelector<HTMLElement>(target)
      : target

    if (!element) {
      logWarn(
        new Error(`eco-suite-navbar-skip-to-content: target "${String(target)}" did not resolve to an element`),
        { source: 'EcoSuiteNavbarSkipToContent.handleSkip' },
      )
      return
    }

    // Make element focusable if it isn't already natively or explicitly
    if (!element.matches('a[href], button, input, select, textarea, [tabindex]')) {
      element.setAttribute('tabindex', '-1')
    }
    element.focus()
  }

  render() {
    if (!this.targets.length) return nothing

    return html`
      <a
        href="#"
        class="SkipToContent__trigger"
        aria-label=${$t('navbar.skipTo')}
        aria-haspopup="menu"
        aria-expanded=${this.visible}
        aria-controls=${EcoSuiteNavbarSkipToContent._popoverId}
        @focus=${this.handleTriggerFocus}
        @keydown=${this.handleKeydown}
        @click=${(e: Event) => e.preventDefault()}
      ></a>
      <div
        id=${EcoSuiteNavbarSkipToContent._popoverId}
        class=${classMap({
          SkipToContent__popover: true,
          'SkipToContent__popover--visible': this.visible,
        })}
        role="navigation"
        aria-label=${$t('navbar.skipTo')}
        @focusout=${this.handlePopoverFocusout}
        @keydown=${this.handleKeydown}
      >
        <span class="SkipToContent__label">${$t('navbar.skipTo')}:</span>
        ${repeat(
          this.targets,
          (t) => t.target,
          (t) => html`
            <button
              class="SkipToContent__link"
              @click=${() => this.handleSkip(t.target)}
            >${t.label}</button>
          `,
        )}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'eco-suite-navbar-skip-to-content': EcoSuiteNavbarSkipToContent
  }
}
